// Resources Explorer (F4b) pure helpers: sizes, unique "(n)" names, upload name-conflict decisions,
// version lists and zip file names. No Vue / store imports so they can be unit tested directly.

var UNITS = ['KB', 'MB', 'GB', 'TB']

// 532 -> "532 B", 2048 -> "2 KB", 1572864 -> "1.5 MB". null / undefined / invalid -> "—".
export function formatSize (bytes) {
  if (bytes === null || bytes === undefined || bytes === '') return '—'
  var n = Number(bytes)
  if (!isFinite(n) || n < 0) return '—'
  if (n < 1024) return Math.round(n) + ' B'
  var i = -1
  do {
    n = n / 1024
    i++
  } while (n >= 1024 && i < UNITS.length - 1)
  var text = n >= 100 || i === 0 ? String(Math.round(n)) : String(Math.round(n * 10) / 10)
  return text + ' ' + UNITS[i]
}

// Size of a node for display: folders and legacy files (sizeBytes null) -> "—".
export function nodeSize (node) {
  if (!node || node.kind !== 'file' || !node.raw) return '—'
  return formatSize(node.raw.sizeBytes)
}

function splitName (name) {
  var s = String(name || '')
  var dot = s.lastIndexOf('.')
  if (dot <= 0) return { base: s, ext: '' }
  return { base: s.slice(0, dot), ext: s.slice(dot) }
}

// "Report.pdf" + taken ["report.pdf", "Report (2).pdf"] -> "Report (3).pdf" (case-insensitive).
// A name that already ends in " (n)" continues counting from n + 1.
export function uniqueName (name, taken) {
  var set = {}
  ;(taken || []).forEach(function (t) {
    if (t !== null && t !== undefined) set[String(t).toLowerCase()] = true
  })
  var original = String(name || '')
  if (!set[original.toLowerCase()]) return original
  var parts = splitName(original)
  var base = parts.base
  var n = 2
  var m = /^(.*) \((\d+)\)$/.exec(base)
  if (m && m[1]) {
    base = m[1]
    n = parseInt(m[2], 10) + 1
  }
  var candidate = base + ' (' + n + ')' + parts.ext
  while (set[candidate.toLowerCase()]) {
    n++
    candidate = base + ' (' + n + ')' + parts.ext
  }
  return candidate
}

// ---------- upload name conflicts ----------

// keep = upload with a unique "(n)" name, replace = overwrite (no history),
// version = upload as a new version, skip = do not upload this file (Cancel)
export var CONFLICT_CHOICES = ['keep', 'replace', 'version', 'skip']
export var DEFAULT_CONFLICT_CHOICE = 'version'

function normalizeChoice (c) {
  return CONFLICT_CHOICES.indexOf(c) === -1 ? 'skip' : c
}

// One decider per upload batch. Once the user ticks "Apply to all remaining conflicts",
// that choice is reused for every later conflict of the same batch without asking.
export function createConflictDecider () {
  var applied = null
  return {
    preset: function () {
      return applied
    },
    // answer: { choice, applyAll } -> the choice to use for this conflict
    record: function (answer) {
      var choice = normalizeChoice(answer && answer.choice)
      if (answer && answer.applyAll === true) applied = choice
      return choice
    }
  }
}

// Asks for each conflict in turn (unless a preset applies).
// ask(conflict, remaining) -> Promise<{ choice, applyAll }>; remaining = conflicts after this one.
// Resolves to [{ conflict, choice }] in the same order. A rejected ask counts as "skip".
export function resolveConflicts (conflicts, decider, ask) {
  var list = conflicts || []
  var out = []
  var i = 0
  function next () {
    if (i >= list.length) return Promise.resolve(out)
    var conflict = list[i]
    var remaining = list.length - i - 1
    i++
    var preset = decider.preset()
    if (preset) {
      out.push({ conflict: conflict, choice: preset })
      return next()
    }
    return Promise.resolve()
      .then(function () { return ask(conflict, remaining) })
      .then(function (answer) {
        return decider.record(answer)
      }, function () {
        return 'skip'
      })
      .then(function (choice) {
        out.push({ conflict: conflict, choice: choice })
        return next()
      })
  }
  return next()
}

// ---------- versions ----------

function versionNumber (v) {
  var n = Number(v && v.number)
  return isFinite(n) ? n : 0
}

// Current version number: file.fileVersion when present, otherwise the highest number.
export function currentVersionNumber (file) {
  var f = file || {}
  var n = Number(f.fileVersion)
  if (f.fileVersion !== null && f.fileVersion !== undefined && isFinite(n) && n > 0) return n
  var max = 0
  ;(Array.isArray(f.versions) ? f.versions : []).forEach(function (v) {
    max = Math.max(max, versionNumber(v))
  })
  return max
}

// Newest first, each tagged { ...version, isCurrent }
export function sortedVersions (file) {
  var f = file || {}
  var cur = currentVersionNumber(f)
  return (Array.isArray(f.versions) ? f.versions : [])
    .slice()
    .sort(function (a, b) { return versionNumber(b) - versionNumber(a) })
    .map(function (v) { return Object.assign({}, v, { isCurrent: versionNumber(v) === cur }) })
}

// ---------- zip ----------

// Content-Disposition -> file name (RFC 5987 filename* preferred), else fallback.
export function fileNameFromDisposition (header, fallback) {
  var h = String(header || '')
  var star = /filename\*\s*=\s*(?:UTF-8|utf-8)?''([^;]+)/.exec(h)
  if (star && star[1]) {
    try {
      return decodeURIComponent(star[1].trim().replace(/^"|"$/g, ''))
    } catch (e) { /* fall through */ }
  }
  var plain = /filename\s*=\s*("([^"]*)"|[^;]+)/.exec(h)
  if (plain) {
    var v = (plain[2] !== undefined ? plain[2] : plain[1]).trim()
    if (v) return v
  }
  return fallback
}
